// src/components/user/UserForm.js
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  Paper,
  Typography,
  FormHelperText
} from '@mui/material';
import { getCompanies } from '../../api/companyAPI';
import { useAuth } from '../../contexts/AuthContext';

const UserForm = ({ 
  initialData = null, 
  onSubmit, 
  isLoading = false,
  isEdit = false 
}) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_id: '',
    role: 'Driver',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Fetch companies for dropdown - get all companies
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies(), // Call without enabled parameter to get all companies
    enabled: currentUser?.role === 'SuperAdmin' || currentUser?.role === 'Admin'
  });

  // Initialize form with existing data for edit mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        // For SuperAdmin users, company_id should be empty string for form handling
        company_id: initialData.role === 'SuperAdmin' ? '' : (initialData.company_id || ''),
        role: initialData.role || 'Driver',
        password: '',
        confirmPassword: ''
      });
    } else if (!isEdit && currentUser?.role === 'Admin' && currentUser?.company_id) {
      // For Admin users creating new users, pre-select their company
      setFormData(prev => ({
        ...prev,
        company_id: currentUser.company_id
      }));
    }
  }, [initialData, isEdit, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for role changes
    if (name === 'role') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        // Clear company_id when SuperAdmin is selected
        company_id: value === 'SuperAdmin' ? '' : prev.company_id
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!isEdit && !formData.email.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!isEdit && !formData.password) {
      newErrors.password = 'Password is required';
    }

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only for create or when password is being changed)
    if (formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Company validation based on role
    if (formData.role === 'SuperAdmin') {
      // SuperAdmin must NOT have a company_id
      if (formData.company_id) {
        newErrors.company_id = 'SuperAdmin users cannot be assigned to a company';
      }
    } else {
      // Admin and Driver must have a company_id
      if (!formData.company_id) {
        newErrors.company_id = `Company is required for ${formData.role} role`;
      }
    }

    // Role validation based on current user permissions
    if (currentUser?.role === 'Admin' && formData.role === 'SuperAdmin') {
      newErrors.role = 'Admins cannot create SuperAdmin users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const submitData = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone: formData.phone.trim() || null,
      // Company ID handling based on role
      company_id: formData.role === 'SuperAdmin' ? null : parseInt(formData.company_id)
    };

    // Add email for create mode
    if (!isEdit) {
      submitData.email = formData.email.trim();
      submitData.role = formData.role;
    }

    // Add password only if provided
    if (formData.password) {
      submitData.password = formData.password;
    }

    onSubmit(submitData);
  };

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    if (currentUser?.role === 'SuperAdmin') {
      return ['SuperAdmin', 'Admin', 'Driver'];
    } else if (currentUser?.role === 'Admin') {
      return ['Admin', 'Driver'];
    }
    return ['Driver'];
  };

  // Get available companies based on current user's role
  const getAvailableCompanies = () => {
    if (currentUser?.role === 'SuperAdmin') {
      return companies;
    } else if (currentUser?.role === 'Admin') {
      // Admin can only assign users to their own company
      return companies.filter(company => company.CompanyId === currentUser.company_id);
    }
    return [];
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {isEdit ? 'Edit User Information' : 'Create New User'}
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Personal Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required={!isEdit}
              fullWidth
              type="email"
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isEdit} // Email cannot be changed in edit mode
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>

          {/* Role and Company */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Role and Company
            </Typography>
          </Grid>
          
          {!isEdit && (
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                select
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                error={!!errors.role}
                helperText={errors.role}
              >
                {getAvailableRoles().map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          
          {formData.role !== 'SuperAdmin' && (
            <Grid item xs={12} sm={isEdit ? 12 : 6}>
              <TextField
                required
                fullWidth
                select
                label="Company"
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                error={!!errors.company_id}
                helperText={errors.company_id}
                disabled={currentUser?.role === 'Admin' && currentUser?.company_id}
              >
                {getAvailableCompanies().map((company) => (
                  <MenuItem key={company.CompanyId} value={company.CompanyId}>
                    {company.CompanyName}
                  </MenuItem>
                ))}
              </TextField>
              {currentUser?.role === 'Admin' && (
                <FormHelperText>
                  As an Admin, you can only assign users to your company
                </FormHelperText>
              )}
            </Grid>
          )}

          {formData.role === 'SuperAdmin' && (
            <Grid item xs={12} sm={isEdit ? 12 : 6}>
              <Alert severity="info" sx={{ mt: 1 }}>
                SuperAdmin users are not assigned to any specific company and have system-wide access.
              </Alert>
            </Grid>
          )}

          {/* Password Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              {isEdit ? 'Change Password (Optional)' : 'Password'}
            </Typography>
            {isEdit && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Leave blank to keep current password
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required={!isEdit}
              fullWidth
              type="password"
              label={isEdit ? "New Password" : "Password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required={!isEdit && formData.password}
              fullWidth
              type="password"
              label="Confirm Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disabled={!formData.password}
            />
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => window.history.back()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading 
                  ? (isEdit ? 'Updating...' : 'Creating...') 
                  : (isEdit ? 'Update User' : 'Create User')
                }
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default UserForm;